import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSearchParams } from 'react-router-dom';
import { Image } from '../../types';
import ImageCard from './ImageCard';
import { api } from '../../services/api';
import { usePrices } from '../../context/PriceContext';

const GalleryContainer = styled.div`
  padding: 1rem 2rem;
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr); /* Reducido a 4 columnas para imágenes más grandes */
  grid-auto-rows: minmax(100px, auto);
  grid-gap: 1.5rem; /* Mayor espacio entre imágenes */
  
  /* Esto permite que las imágenes verticales ocupen más espacio verticalmente */
  grid-auto-flow: dense;
  
  @media (max-width: 1600px) {
    grid-template-columns: repeat(4, 1fr);
  }
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem 0;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.text.secondary};
  background-color: transparent;
`;

interface ImageGalleryProps {
  searchQuery?: string;
  onResultsStatus?: (hasResults: boolean) => void;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ searchQuery = '', onResultsStatus }) => {
  const [images, setImages] = useState<Image[]>([]);
  const [page, setPage] = useState(0); // Cambiado a 0 porque la API comienza desde la página 0
  const [pageSize] = useState(40); // Aumentado a 40 imágenes por carga
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  
  const { getPriceByType } = usePrices();
  const [searchParams] = useSearchParams();
  const shuffleSeed = parseInt(searchParams.get('seed') || '0', 10);

  const loadImages = async () => {
    setLoading(true);
    try {
      // Usar el precio del contexto en lugar de hacer llamadas a la API de precios
      const individualPrice = getPriceByType('individual');
      
      const newImages = searchQuery
        ? await api.searchImages(searchQuery, page, pageSize, individualPrice)
        : await api.getFeaturedImages(page, pageSize, individualPrice, shuffleSeed);

      if (newImages.length === 0) {
        setHasMore(false);
      } else {
        setImages(prevImages => [...prevImages, ...newImages]);
        setPage(prevPage => prevPage + 1); // Incremento correcto de 1 en 1
      }

      // Notificar al componente padre sobre el estado de los resultados
      if (onResultsStatus && page === 0) {
        onResultsStatus(newImages.length > 0);
      }
    } catch (error) {
      console.error('Error loading images:', error);
      // Notificar al componente padre en caso de error
      if (onResultsStatus && page === 0) {
        onResultsStatus(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset gallery when search query or seed changes
  useEffect(() => {
    setImages([]);
    setPage(0);
    setHasMore(true);
  }, [searchQuery, shuffleSeed]);

  // Load initial images
  useEffect(() => {
    if (page === 0) {
      loadImages();
    }
  }, [page, searchQuery, shuffleSeed]);

  // Notificar al componente padre sobre el estado de los resultados
  useEffect(() => {
    if (!loading && onResultsStatus) {
      onResultsStatus(images.length > 0);
    }
  }, [images, loading, onResultsStatus]);

  // Debugging
  useEffect(() => {
    console.log('Current images:', images);
  }, [images]);

  return (
    <GalleryContainer>
      <InfiniteScroll
        dataLength={images.length}
        next={loadImages}
        hasMore={hasMore}
        loader={<LoadingIndicator>Cargando más imágenes...</LoadingIndicator>}
        endMessage={
          <LoadingIndicator>
            {images.length === 0 && searchQuery
              ? 'No se encontraron imágenes que coincidan con esta búsqueda'
              : images.length === 0 
                ? '' 
                : 'No hay más imágenes para mostrar'}
          </LoadingIndicator>
        }
      >
        <ImagesGrid>
          {images.map(image => (
            <ImageCard key={image.id} image={image} />
          ))}
        </ImagesGrid>
      </InfiniteScroll>
    </GalleryContainer>
  );
};

export default ImageGallery;